import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "./components/Sidebar";
import Main from "./pages/main";
import Login from "./pages/signin";
import DealsHeader from "./pages/deals/index";
import DealsPage from "./pages/deals";
import ReleasePage from "./pages/release";
import ProductSheetPage from "./pages/product-sheet";
import DocumentsPage from "./pages/documents";
import WebinarPage from "./pages/webinar";
import WatchPage from "./pages/webinar/WatchPage";
import AcademyPage from "./pages/academy";
import CoursePage from "./pages/academy/CoursePage";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedDealsRoute from "./components/ProtectedDealsRoute";
import { useAuth } from "./auth/AuthContext";
import "./App.css";
import GraphicsPage from "./pages/graphics";
import Item from "./pages/brand-guide";
import Dashboard from "./Dashboard";
import { WhatsNewProvider } from "./hooks/WhatsNewContext";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <WhatsNewProvider>
                <div className="flex h-screen bg-gray-50">
                  <Sidebar />
                  <div className="flex-1 ml-72 min-h-0 h-screen overflow-y-auto">
                    <Dashboard />
                  </div>
                </div>
              </WhatsNewProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <WhatsNewProvider>
                <div className="flex h-screen bg-gray-50">
                  <Sidebar />
                  <div className="flex-1 ml-72 min-h-0 h-screen overflow-y-auto">
                    <Routes>
                    <Route path="main" element={<Main />} />
                    <Route
                      path="deals"
                      element={
                        <ProtectedDealsRoute>
                          <DealsPage />
                        </ProtectedDealsRoute>
                      }
                    />
                    <Route path="release" element={<ReleasePage />} />
                    <Route
                      path="product-sheet"
                      element={<ProductSheetPage />}
                    />
                    <Route path="documents" element={<DocumentsPage />} />
                    <Route path="webinar" element={<WebinarPage />} />
                    <Route path="webinar/:id" element={<WatchPage />} />
                    <Route path="academy" element={<AcademyPage />} />
                    <Route path="academy/:id" element={<CoursePage />} />
                    <Route path="graphics" element={<GraphicsPage />} />
                    <Route path="brand-guide" element={<Item />} />
                    </Routes>
                  </div>
                </div>
              </WhatsNewProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
